import { memo, useEffect, useMemo, useRef } from 'react';
import { Map as MapLibre, Marker, Source, Layer, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

import { NICE_ANCHOR } from '../../../config/geo';

const mapKey = process.env.REACT_APP_MAPTILER_KEY;
const mapStyle = `https://api.maptiler.com/maps/streets-v2/style.json?key=${mapKey}`;

const CENTER = { lat: NICE_ANCHOR.lat, lng: NICE_ANCHOR.lng };

// 64-point polygon approximating a circle at (lat, lng) with radius in km.
// Accurate enough at the scale of a French département.
const buildCirclePolygon = (lat, lng, radiusKm, steps = 64) => {
  const coords = [];
  const earthRadiusKm = 6371;
  const latRad = (lat * Math.PI) / 180;
  for (let i = 0; i <= steps; i++) {
    const bearing = (i / steps) * 2 * Math.PI;
    const dx = (radiusKm / earthRadiusKm) * Math.sin(bearing);
    const dy = (radiusKm / earthRadiusKm) * Math.cos(bearing);
    const dLng = (dx / Math.cos(latRad)) * (180 / Math.PI);
    const dLat = dy * (180 / Math.PI);
    coords.push([lng + dLng, lat + dLat]);
  }
  return {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [coords] },
    properties: {},
  };
};

const circleFillPaint = {
  'fill-color': '#848cba',
  'fill-opacity': 0.15,
};
const circleLinePaint = {
  'line-color': '#848cba',
  'line-opacity': 0.9,
  'line-width': 2,
};

const ScopeMapInner = ({ radiusKm, groups = [] }) => {
  const mapRef = useRef(null);

  const circleGeoJson = useMemo(
    () => buildCirclePolygon(CENTER.lat, CENTER.lng, radiusKm || 0),
    [radiusKm]
  );

  useEffect(() => {
    if (mapRef.current && radiusKm) {
      const zoom = Math.max(6, 12 - Math.log2(radiusKm / 5));
      mapRef.current.setZoom(Math.round(zoom));
    }
  }, [radiusKm]);

  return (
    <div className="w-full h-[320px] rounded-md overflow-hidden">
      <MapLibre
        initialViewState={{
          longitude: CENTER.lng,
          latitude: CENTER.lat,
          zoom: 9,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        onLoad={evt => { mapRef.current = evt.target; }}
        attributionControl={false}
      >
        <NavigationControl position="top-right" showCompass={false} />

        <Source id="scope-circle" type="geojson" data={circleGeoJson}>
          <Layer id="scope-circle-fill" type="fill" paint={circleFillPaint} />
          <Layer id="scope-circle-line" type="line" paint={circleLinePaint} />
        </Source>

        <Marker longitude={CENTER.lng} latitude={CENTER.lat} anchor="bottom">
          <div
            title={NICE_ANCHOR.name}
            style={{
              background: '#e74c3c',
              color: '#fff',
              borderRadius: '50%',
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 12,
              boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
            }}
          >
            N
          </div>
        </Marker>

        {groups.map((g, i) =>
          g.lat != null && g.lng != null ? (
            <Marker
              key={`${g.city}-${i}`}
              longitude={Number(g.lng)}
              latitude={Number(g.lat)}
              anchor="bottom"
            >
              <div
                title={`${g.city} · ${g.total} records`}
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: '#3b82f6',
                  border: '2px solid #fff',
                  opacity: 0.85,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              />
            </Marker>
          ) : null
        )}
      </MapLibre>
    </div>
  );
};

export const ScopeMap = memo(ScopeMapInner);
